import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { Search, Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import AdminLayout from '@/layouts/AdminLayout'

interface User {
  id: string
  name: string
  email: string
  role: 'admin' | 'operator' | null
  status: string
  super_admin: boolean
  organization_name: string | null
  organization_slug: string | null
  organization_id: string | null
  avatar_initials: string
  created_at: string
}

interface Props {
  users: User[]
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function UsersIndex({ users }: Props) {
  const [search, setSearch] = useState('')

  const filtered = users.filter((user) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      user.name.toLowerCase().includes(q) ||
      user.email.toLowerCase().includes(q) ||
      (user.organization_name || '').toLowerCase().includes(q)
    )
  })

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Users</h1>
      </header>

      {/* Toolbar */}
      <div className="flex items-center gap-4 border-b px-8 py-3">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-xs"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {filtered.length} {filtered.length === 1 ? 'user' : 'users'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">User</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Email</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Organization</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Role</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Created</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => (
              <tr key={user.id} className="border-b hover:bg-muted/30">
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white bg-blue-500"
                    >
                      {user.avatar_initials}
                    </div>
                    <span className="text-[13px] font-medium text-foreground">
                      {user.name}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-3 text-[13px] text-muted-foreground">{user.email}</td>
                <td className="px-6 py-3 text-[13px]">
                  {user.organization_id ? (
                    <Link
                      href={`/admin/organizations/${user.organization_id}`}
                      className="text-foreground hover:text-blue-600 hover:underline cursor-pointer"
                    >
                      {user.organization_name}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-6 py-3">
                  {user.super_admin ? (
                    <Badge
                      variant="secondary"
                      size="sm"
                      className="bg-amber-50 text-amber-800"
                    >
                      Super Admin
                    </Badge>
                  ) : user.role === 'admin' ? (
                    <Badge
                      variant="secondary"
                      size="sm"
                      className="bg-blue-50 text-[#1e3a5f]"
                    >
                      Admin
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      size="sm"
                      className="bg-gray-100 text-gray-600"
                    >
                      Operator
                    </Badge>
                  )}
                </td>
                <td className="px-6 py-3">
                  {user.status === 'active' ? (
                    <Badge
                      variant="secondary"
                      size="sm"
                      className="bg-emerald-50 text-emerald-600"
                    >
                      Active
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      size="sm"
                      className="bg-red-100 text-red-800"
                    >
                      Deactivated
                    </Badge>
                  )}
                </td>
                <td className="px-6 py-3 text-[13px] text-muted-foreground">{formatDate(user.created_at)}</td>
                <td className="px-6 py-3 text-right">
                  {user.super_admin ? (
                    <span className="text-muted-foreground/40 cursor-not-allowed" title="Super admin accounts are managed via Account settings">
                      <Pencil className="h-3.5 w-3.5" />
                    </span>
                  ) : (
                    <Link
                      href={`/admin/users/${user.id}/edit`}
                      className="text-muted-foreground hover:text-foreground cursor-pointer"
                      title="Edit user"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

UsersIndex.layout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
)
